import React, { useContext, useEffect, useState } from 'react';
import CardContainer from '../../../../components/redesign/CardContainer';
import { KpiData } from 'components/redesign/KpiTile/types';
import KpiGrid from '../../../../components/redesign/KpiGrid';
import KpiDefinitionData from 'service/KpiDefinitionData';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import KpiTile from 'components/redesign/KpiTile';
import avgApiResponseTime from '../../../../v2/kpis/pepiris/avgApiResponseTime';
import totalNumberofErrors from '../../../../v2/kpis/pepiris/totalNumberofErrors';
import applicationErrors from '../../../../v2/kpis/rgm/pricingai/appPerformance/applicationErrors';
import { useDynamicGraphQLQuery } from '../../../../hooks/useDynamicGraphQLQuery';
import { constants } from 'constants/Constants';
// Import query to ensure it's registered

interface ApplicationPerformanceScorecardProps {
  title: string;
  showInfoIcon?: boolean;
  isOverview?: boolean; // New prop to indicate if it's used in an overview context
}
const ApplicationPerformanceScorecard: React.FC<
  ApplicationPerformanceScorecardProps
> = ({ title, showInfoIcon = true, isOverview = false }) => {
  const { selectedProduct, selectedApplicationNames, selectedSector } =
    useContext(FilterContext);

  const [overviewConfig, setOverviewConfig] = useState<KpiData[]>([]);

  // Using the dynamic GraphQL query instead of the useApplicationPerformance hook
  const { data: appPerformScoreCardData, isLoading: performanceLoading } =
    useDynamicGraphQLQuery(
      'getAppPerformanceScorecard',
      {
        platforms: selectedApplicationNames,
        sectorId:
          selectedProduct?.productId === constants.products.ProfitToServe
            ? null
            : selectedSector?.sectorId,
      },
      true,
      'StandardMetrics/appPerformance'
    );
  const generateScoreCardItems = (data: any) => {
    const items = [
      avgApiResponseTime.handler(data),
      totalNumberofErrors.handler(data),
    ];

    // Add applicationErrors KPI for pricing AI product if the data is available
    if (
      selectedProduct?.productId === constants.products.PricingAI &&
      data.applicationErrors !== undefined &&
      data.applicationErrors !== null
    ) {
      items.push(applicationErrors.handler(data));
    }

    return items;
  };
  useEffect(() => {
    // Extract data directly from the response to match the structure expected by handlers
    const {
      avgApiAppResponseTime,
      avgApiAppResponseTimeDelta,
      totalNoOfErrors,
      totalNoOfErrorsDelta,
      applicationErrors,
      applicationErrorsDelta,
    } = appPerformScoreCardData?.getAppPerformanceScorecard?.data || {};

    // Pass the extracted data directly to handlers
    setOverviewConfig(
      generateScoreCardItems({
        avgApiAppResponseTime,
        avgApiAppResponseTimeDelta,
        totalNoOfErrors,
        totalNoOfErrorsDelta,
        applicationErrors,
        applicationErrorsDelta,
      })
    );
  }, [appPerformScoreCardData]);
  const getTitleSubText = (
    selectedProduct: any,
    constants: any
  ): string | undefined => {
    if (
      selectedProduct?.productId ===
        constants?.products?.ManufacturingControlTower &&
      selectedSector?.sectorId === constants.sectors.LATAM
    ) {
      return '*Applicable at sector level only';
    } else if (
      selectedProduct?.productId === constants.products.ProfitToServe &&
      isOverview
    ) {
      return '*Data available at Product level only';
    }
    return undefined;
  };

  const [titleSubText, setTitleSubText] = useState<string | undefined>(
    getTitleSubText(selectedProduct, constants)
  );

  useEffect(() => {
    setTitleSubText(getTitleSubText(selectedProduct, constants));
  }, [selectedSector, selectedProduct, constants]);

  // Set columns based on product type
  const getColumnsConfig = () => {
    if (selectedProduct?.productId === constants.products.PricingAI) {
      return { columnsDesktop: 3, columnsTablet: 3, columnsMobile: 2 };
    }
    return { columnsDesktop: 2, columnsTablet: 2, columnsMobile: 2 };
  };

  const columnsConfig = getColumnsConfig();

  return (
    <CardContainer
      title={showInfoIcon ? title : ''}
      titleSubText={titleSubText}
      showInfoIcon={showInfoIcon}
      dataDefinitions={{
        sections:
          KpiDefinitionData[selectedProduct?.productId]
            ?.applicationPerformanceRedesigned?.scoreCard ?? [],
      }}
      isAccordion={false}
      isLoading={performanceLoading}
    >
      <KpiGrid
        columnsDesktop={columnsConfig.columnsDesktop}
        columnsTablet={columnsConfig.columnsTablet}
        columnsMobile={columnsConfig.columnsMobile}
      >
        {overviewConfig.map((item, index) => (
          <KpiTile key={index} data={item} />
        ))}
      </KpiGrid>
    </CardContainer>
  );
};

export default ApplicationPerformanceScorecard;
